// src/components/BusinessCallButton.jsx
import React, { useState, useRef, useEffect } from "react";
import './IncomingCall.css'; // Reuse existing styles
import CallEndIcon from '@mui/icons-material/CallEnd';
// import CallIcon from '@mui/icons-material/Call';
import { useAuth } from "../../Context/Auth";
import useCallSocket from "./hooks/useCallSocket";
import CallIcon from '@mui/icons-material/Call';
import { initiateBusinessCall, terminateCall } from "../../API/CommonApi";


export default function BusinessCallButton({ customerNumber }) {
  const [calleeNumber, setCalleeNumber] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [terminating, setTerminating] = useState(false);
  const [showCallUI, setShowCallUI] = useState(false);
  const [callId, setCallId] = useState(null);
  const pcRef = useRef(null);
  const auth = useAuth();
  const businessNumber = auth.wpDetails?.[0].phone_numbers?.[0]?.phone_number_id;
  const call = useCallSocket(businessNumber);


  async function createOffer() {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" } // Google's public STUN server
      ]
    });
    pcRef.current = pc;
    
    pc.ontrack = (e) => {
      const audio = new Audio();
      audio.srcObject = e.streams[0];
      audio.autoplay = true;
      document.body.appendChild(audio);
    };

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => pc.addTrack(track, stream));
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === "failed") {
        console.warn("ICE failed — no media will flow");
      }
    };


    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Wait for ICE gathering to complete
    await new Promise(resolve => {
        const checkState = () => {
            if (pc.iceGatheringState === "complete") {
                pc.removeEventListener("icegatheringstatechange", checkState);
                resolve();
            }
        };
        pc.addEventListener("icegatheringstatechange", checkState);
    });
    
    return pc.localDescription.sdp;
  }

  useEffect(() => {
    // Check if we have an active PeerConnection and the remote peer has accepted.
    // The webhook handler sends the SDP Answer when status is "accept".
    if (pcRef.current && call?.status === "accept" && call.session?.sdp) {

      try {
          // Create the remote description object
          const remoteDesc = new RTCSessionDescription({
              type: "answer",
              sdp: call.session.sdp // SDP Answer is nested in the payload
          });

          // Apply the remote description.
          pcRef.current.setRemoteDescription(remoteDesc)
              .catch(e => console.error("Error setting remote description:", e));

      } catch (e) {
          console.error("Error processing SDP Answer from WebSocket:", e);
      }
    }
  }, [call]);

  useEffect(() => {
    let timer;
    if (call?.status === "accept") {
      timer = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    if (call?.status === "terminate" || call?.status === "reject") {
      setShowCallUI(false);
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      setCallDuration(0);
      document.querySelectorAll("audio").forEach(audio => audio.remove());
    }
    return () => clearInterval(timer);
  }, [call?.status]);



  useEffect(() => {
    if (customerNumber) {
      setCalleeNumber(customerNumber);
    }
  }, [customerNumber]);

  async function handleCallInitiate() {
    setShowCallUI(true);
    const phone_number_id = auth?.wpDetails[0]?.phone_numbers[0]?.phone_number_id;
    const whatsapp_business_account_id = auth?.wpDetails[0]?.whatsapp_business_account_id
    // Generate SDP Offer locally
    const sdpOffer = await createOffer();

    try {
      // Call Django API endpoint to initiate the call
      const res = await initiateBusinessCall({
        to_number: calleeNumber,
        sdp_offer: sdpOffer,
        phone_number_id,
        whatsapp_business_account_id
      });
      
      const data = await res.json();

      //set callId to state
      if (data.call_id) {
        setCallId(data.call_id);
      }
    } catch (err) {
      console.error("Initiate call error:", err);
    }
  }

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  async function handleTerminate() {
    setTerminating(true);
    try {
      await terminateCall(call?.call_id);
    } catch (err) {
      console.error("Call terminate error:", err);
    }
    setTerminating(false);
    setShowCallUI(false);
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    document.querySelectorAll("audio").forEach(audio => audio.remove());
  }

  return (
    <div>
      <button className="whatsapp-call-button" onClick={handleCallInitiate} disabled={showCallUI}>
        <CallIcon style={{ marginRight: '8px' }} />
        Call
      </button>


          
      <div className="call-popup-overlay">
        {showCallUI  && (
          <div className="call-popup-container">
            <h3 className="caller-name">{auth.selectedChat?.contact?.name}</h3>
            <h5 className="caller-number">{auth.selectedChat?.contact?.phone_number}</h5>
            {call?.status === "accept" && (
              <p className="call-timer">Duration: {formatDuration(callDuration)}</p>
            )}
            <p className="call-status">
              {!terminating && (call?.status === "connect" ? <p>Ringing...</p> : call?.status === "accept" ? <p>Call in progress...</p> : <p>Preparing call...</p>)}
              {terminating && <p>Terminating...</p>}
            </p>
            <div className="call-actions">
              {(call?.status === "connect" || call?.status === "accept") && (
                <button className="reject-btn" onClick={handleTerminate}>
                  <CallEndIcon disabled={terminating} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
